import { FormEvent, useEffect, useId, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../../api/client";
import { Field } from '../../components/forms/Field';
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { TagDisplaySettings } from "../../components/tags/TagDisplaySettings";
import { apiError } from "../../lib/apiError";
import { ACCOUNT_DELETION_DAYS } from "../../lib/contact";
import { APP_CONTENT_CLEAR_PHRASE, type AppContentStatus } from "../../lib/deletion";
import { passwordFieldProps } from "../../lib/inputPrivacy";
import {
  clearAdminPasswordVerifier,
  storeAdminPasswordVerifier,
} from "../../lib/offline/offlineAdminPassword";
import type { AdminPasswordStatus } from "../../types";

type Panel = "login" | "admin" | "ops" | "mfa" | "clear" | "restore" | "delete-account" | null;

type MfaStatus = {
  mfa_enabled: boolean;
  pending_setup: boolean;
};

type MfaSetup = {
  secret: string;
  qr_code: string;
  backup_codes: string[];
};

const EMPTY_LOGIN = {
  current: "",
  next: "",
  confirm: "",
};

const EMPTY_ADMIN = {
  login: "",
  current: "",
  next: "",
  confirm: "",
};

export function SettingsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<AdminPasswordStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [openPanel, setOpenPanel] = useState<Panel>(null);
  const [loginForm, setLoginForm] = useState(EMPTY_LOGIN);
  const [adminForm, setAdminForm] = useState(EMPTY_ADMIN);
  const [opsForm, setOpsForm] = useState({
    skip_birth_edit_password: true,
    login_password: "",
  });
  const [mfaStatus, setMfaStatus] = useState<MfaStatus | null>(null);
  const [mfaSetup, setMfaSetup] = useState<MfaSetup | null>(null);
  const [mfaEnableCode, setMfaEnableCode] = useState("");
  const [mfaDisablePassword, setMfaDisablePassword] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [appContentStatus, setAppContentStatus] = useState<AppContentStatus | null>(null);
  const [clearForm, setClearForm] = useState({ password: "", confirm_phrase: "", understood: false });
  const [restoreForm, setRestoreForm] = useState({ password: "" });
  const [deleteAccountForm, setDeleteAccountForm] = useState({
    email: "",
    login_password: "",
    understood: false,
  });
  const loginFormId = useId();
  const adminFormId = useId();
  const opsFormId = useId();
  const mfaFormId = useId();
  const fieldNs = useId().replace(/:/g, "");

  async function load() {
    const [adminRes, prefsRes, mfaRes, meRes, clearStatusRes] = await Promise.all([
      api.GET("/api/settings/admin-password"),
      api.GET("/api/birth-preferences"),
      api.GET("/api/auth/mfa/status"),
      api.GET("/api/me"),
      api.GET("/api/data/clear-app-content/status"),
    ]);
    if (adminRes.error) {
      setError(apiError(adminRes.error, "Nie udało się wczytać ustawień."));
      return;
    }
    setError(null);
    setStatus(adminRes.data as AdminPasswordStatus);
    if (!mfaRes.error) {
      setMfaStatus(mfaRes.data as MfaStatus);
    }
    if (!meRes.error) {
      const me = meRes.data as { email?: string };
      setUserEmail(me.email ?? "");
      setDeleteAccountForm((current) => ({
        ...current,
        email: me.email ?? "",
      }));
    }
    if (!clearStatusRes.error) {
      setAppContentStatus(clearStatusRes.data as AppContentStatus);
    }
    if (!prefsRes.error) {
      const prefs = prefsRes.data as { skip_birth_edit_password?: boolean };
      setOpsForm((current) => ({
        ...current,
        skip_birth_edit_password: Boolean(prefs.skip_birth_edit_password),
      }));
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function closeLoginPanel() {
    setOpenPanel((current) => (current === "login" ? null : current));
    setLoginForm(EMPTY_LOGIN);
  }

  function closeAdminPanel() {
    setOpenPanel((current) => (current === "admin" ? null : current));
    setAdminForm(EMPTY_ADMIN);
  }

  function closeOpsPanel() {
    setOpenPanel((current) => (current === "ops" ? null : current));
    setOpsForm((current) => ({ ...current, login_password: "" }));
  }

  function closeMfaPanel() {
    setOpenPanel((current) => (current === "mfa" ? null : current));
    setMfaSetup(null);
    setMfaEnableCode("");
    setMfaDisablePassword("");
  }

  function togglePanel(panel: Panel) {
    setError(null);
    setInfo(null);
    if (openPanel === panel) {
      if (panel === "login") {
        closeLoginPanel();
      } else if (panel === "admin") {
        closeAdminPanel();
      } else if (panel === "ops") {
        closeOpsPanel();
      } else if (panel === "mfa") {
        closeMfaPanel();
      }
      return;
    }
    setLoginForm(EMPTY_LOGIN);
    setAdminForm(EMPTY_ADMIN);
    setOpsForm((current) => ({ ...current, login_password: "" }));
    setMfaSetup(null);
    setMfaEnableCode("");
    setMfaDisablePassword("");
    setOpenPanel(panel);
  }

  async function onMfaSetup() {
    setBusy(true);
    setInfo(null);
    const result = await api.POST("/api/auth/mfa/setup");
    setBusy(false);
    if (result.error) {
      setError(apiError(result.error, "Nie udało się rozpocząć konfiguracji MFA."));
      return;
    }
    setMfaSetup(result.data as MfaSetup);
    setError(null);
    setInfo("Zeskanuj kod QR w aplikacji uwierzytelniającej i wprowadź kod weryfikacyjny.");
  }

  async function onMfaEnable(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setInfo(null);
    const result = await api.POST("/api/auth/mfa/enable", {
      body: { code: mfaEnableCode.trim() },
    });
    setBusy(false);
    if (result.error) {
      setError(apiError(result.error, "Nie udało się włączyć MFA."));
      return;
    }
    closeMfaPanel();
    setOpenPanel(null);
    setError(null);
    setInfo("Uwierzytelnianie dwuskładnikowe (MFA) zostało włączone.");
    await load();
  }

  async function onMfaDisable(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setInfo(null);
    const result = await api.POST("/api/auth/mfa/disable", {
      body: { password: mfaDisablePassword },
    });
    setBusy(false);
    if (result.error) {
      setError(apiError(result.error, "Nie udało się wyłączyć MFA."));
      return;
    }
    closeMfaPanel();
    setOpenPanel(null);
    setError(null);
    setInfo("MFA zostało wyłączone.");
    await load();
  }

  async function onLoginPassword(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setInfo(null);
    const updated = await api.PUT("/api/settings/login-password", {
      body: {
        current_password: loginForm.current || null,
        new_password: loginForm.next,
        new_password_confirm: loginForm.confirm,
      },
    });
    setBusy(false);
    if (updated.error) {
      setError(apiError(updated.error, "Nie udało się zmienić hasła logowania."));
      return;
    }
    setLoginForm(EMPTY_LOGIN);
    setOpenPanel(null);
    setStatus(updated.data as AdminPasswordStatus);
    setError(null);
    setInfo("Hasło logowania zostało zmienione.");
  }

  async function onAdminPassword(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setInfo(null);
    const updated = await api.PUT("/api/settings/admin-password", {
      body: {
        login_password: adminForm.login || null,
        current_admin_password: adminForm.current || null,
        new_admin_password: adminForm.next,
        new_admin_password_confirm: adminForm.confirm,
      },
    });
    setBusy(false);
    if (updated.error) {
      setError(apiError(updated.error, "Nie udało się zapisać hasła administratora."));
      return;
    }
    setAdminForm(EMPTY_ADMIN);
    setOpenPanel(null);
    setStatus(updated.data as AdminPasswordStatus);
    setError(null);
    clearAdminPasswordVerifier();
    await storeAdminPasswordVerifier(adminForm.next);
    setInfo("Hasło administratora zostało zapisane.");
  }

  async function onOpsPreferences(event: FormEvent) {
    event.preventDefault();
    if (!opsForm.login_password.trim()) {
      setError("Podaj hasło logowania, aby zapisać tę opcję.");
      return;
    }
    setBusy(true);
    setInfo(null);
    const updated = await api.PUT("/api/birth-preferences", {
      body: {
        login_password: opsForm.login_password,
        skip_birth_edit_password: opsForm.skip_birth_edit_password,
      },
    });
    setBusy(false);
    if (updated.error) {
      setError(apiError(updated.error, "Nie udało się zapisać preferencji."));
      return;
    }
    const data = updated.data as { skip_birth_edit_password?: boolean };
    setOpsForm({
      skip_birth_edit_password: Boolean(data.skip_birth_edit_password),
      login_password: "",
    });
    setOpenPanel(null);
    setError(null);
    setInfo("Zapisano preferencje potwierdzania hasłem.");
  }

  async function onClearAppContent(event: FormEvent) {
    event.preventDefault();
    if (!clearForm.understood) {
      setError("Zaznacz potwierdzenie, że rozumiesz skutki operacji.");
      return;
    }
    setBusy(true);
    setError(null);
    const result = await api.POST("/api/data/clear-app-content", {
      body: {
        password: clearForm.password,
        confirm_phrase: clearForm.confirm_phrase,
      },
    });
    setBusy(false);
    if (result.error) {
      setError(apiError(result.error, "Nie udało się wyczyścić danych."));
      return;
    }
    setClearForm({ password: "", confirm_phrase: "", understood: false });
    setOpenPanel(null);
    setInfo("Dane aplikacji zostały wyczyszczone. Możesz je przywrócić w ciągu 14 dni.");
    await load();
  }

  async function onRestoreAppContent(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const result = await api.POST("/api/data/clear-app-content/restore", {
      body: { password: restoreForm.password },
    });
    setBusy(false);
    if (result.error) {
      setError(apiError(result.error, "Nie udało się przywrócić danych."));
      return;
    }
    setRestoreForm({ password: "" });
    setOpenPanel(null);
    setInfo("Przywrócono dane aplikacji.");
    await load();
  }

  async function onDeleteAccount(event: FormEvent) {
    event.preventDefault();
    if (!deleteAccountForm.understood) {
      setError("Zaznacz potwierdzenie usunięcia konta.");
      return;
    }
    setBusy(true);
    setError(null);
    const result = await api.POST("/api/account/deletion-request", {
      body: {
        email: deleteAccountForm.email,
        login_password: deleteAccountForm.login_password || undefined,
      },
    });
    setBusy(false);
    if (result.error) {
      setError(apiError(result.error, "Nie udało się złożyć wniosku o usunięcie konta."));
      return;
    }
    try {
      await fetch("/logout", { credentials: "same-origin", cache: "no-store" });
    } catch {
      /* ignore */
    }
    clearAdminPasswordVerifier();
    navigate("/login?account-deletion=1", { replace: true });
  }

  return (
    <main className="page" data-testid="settings-page">
      <header className="page__header">
        <p className="page__kicker">Konto</p>
        <h1>Ustawienia</h1>
        <p className="muted">
          Hasło logowania otwiera aplikację. Hasło administratora potwierdza trwałe i
          nieodwracalne operacje (usuwanie danych, archiwum).
        </p>
      </header>
      <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
      <AlertBanner variant="ok" message={info} onDismiss={() => setInfo(null)} />

      <TagDisplaySettings />

      <section className="panel settings-panel">
        <div className="panel__header">
          <div>
            <h2 className="panel__title">Hasło logowania</h2>
            <p className="muted">
              {status?.has_login_password
                ? "Hasło logowania jest ustawione."
                : "Hasło logowania nie jest jeszcze ustawione."}
            </p>
          </div>
          <button
            type="button"
            className="btn btn--ghost"
            aria-expanded={openPanel === "login"}
            aria-controls={loginFormId}
            data-testid="settings-login-toggle"
            onClick={() => togglePanel("login")}
          >
            {openPanel === "login"
              ? "Anuluj"
              : status?.has_login_password
                ? "Zmień hasło"
                : "Ustaw hasło"}
          </button>
        </div>

        {openPanel === "login" ? (
          <form
            id={loginFormId}
            className="settings-form"
            onSubmit={onLoginPassword}
            autoComplete="off"
            data-testid="settings-login-form"
          >
            <p className="muted">
              Hasło musi mieć co najmniej 8 znaków i nie może być takie samo jak hasło
              administratora.
            </p>
            {status?.has_login_password ? (
              <Field label="Aktualne hasło logowania">
                <input
                  className="input"
                  type="password"
                  required
                  value={loginForm.current}
                  onChange={(event) =>
                    setLoginForm({ ...loginForm, current: event.target.value })
                  }
                  {...passwordFieldProps(`${fieldNs}-login-current`)}
                />
              </Field>
            ) : null}
            <Field label="Nowe hasło logowania">
              <input
                className="input"
                type="password"
                minLength={8}
                required
                value={loginForm.next}
                onChange={(event) => setLoginForm({ ...loginForm, next: event.target.value })}
                {...passwordFieldProps(`${fieldNs}-login-next`)}
              />
            </Field>
            <Field label="Powtórz nowe hasło logowania">
              <input
                className="input"
                type="password"
                minLength={8}
                required
                value={loginForm.confirm}
                onChange={(event) =>
                  setLoginForm({ ...loginForm, confirm: event.target.value })
                }
                {...passwordFieldProps(`${fieldNs}-login-confirm`)}
              />
            </Field>
            <div className="settings-form__actions">
              <button type="button" className="btn btn--ghost" onClick={closeLoginPanel}>
                Anuluj
              </button>
              <button className="btn" type="submit" disabled={busy}>
                Zapisz hasło logowania
              </button>
            </div>
          </form>
        ) : null}
      </section>

      <section className="panel settings-panel">
        <div className="panel__header">
          <div>
            <h2 className="panel__title">Uwierzytelnianie dwuskładnikowe (MFA)</h2>
            <p className="muted">
              {mfaStatus?.mfa_enabled
                ? "MFA jest włączone — logowanie wymaga kodu z aplikacji uwierzytelniającej."
                : "Dodatkowa ochrona konta kodem TOTP (Google Authenticator, Authy itp.)."}
            </p>
          </div>
          <button
            type="button"
            className="btn btn--ghost"
            aria-expanded={openPanel === "mfa"}
            aria-controls={mfaFormId}
            data-testid="settings-mfa-toggle"
            onClick={() => togglePanel("mfa")}
          >
            {openPanel === "mfa"
              ? "Anuluj"
              : mfaStatus?.mfa_enabled
                ? "Wyłącz MFA"
                : "Skonfiguruj MFA"}
          </button>
        </div>

        {openPanel === "mfa" ? (
          mfaStatus?.mfa_enabled ? (
            <form
              id={mfaFormId}
              className="settings-form"
              onSubmit={(event) => void onMfaDisable(event)}
              autoComplete="off"
              data-testid="settings-mfa-disable-form"
            >
              <p className="muted">
                Wyłączenie MFA wymaga potwierdzenia hasłem logowania.
              </p>
              <Field label="Hasło logowania">
                <input
                  className="input"
                  type="password"
                  required
                  value={mfaDisablePassword}
                  onChange={(event) => setMfaDisablePassword(event.target.value)}
                  data-testid="settings-mfa-disable-password"
                  {...passwordFieldProps(`${fieldNs}-mfa-disable`)}
                />
              </Field>
              <div className="settings-form__actions">
                <button type="button" className="btn btn--ghost" onClick={closeMfaPanel}>
                  Anuluj
                </button>
                <button className="btn" type="submit" disabled={busy} data-testid="settings-mfa-disable-save">
                  Wyłącz MFA
                </button>
              </div>
            </form>
          ) : mfaSetup ? (
            <form
              id={mfaFormId}
              className="settings-form"
              onSubmit={(event) => void onMfaEnable(event)}
              autoComplete="off"
              data-testid="settings-mfa-enable-form"
            >
              <p className="muted">
                Zapisz kody zapasowe w bezpiecznym miejscu — każdy można użyć tylko raz.
              </p>
              <img
                src={mfaSetup.qr_code}
                alt="Kod QR MFA"
                width={200}
                height={200}
                data-testid="settings-mfa-qr"
              />
              <p className="muted">
                Klucz ręczny: <code data-testid="settings-mfa-secret">{mfaSetup.secret}</code>
              </p>
              <pre className="settings-mfa-backup" data-testid="settings-mfa-backup-codes">
                {mfaSetup.backup_codes.join("\n")}
              </pre>
              <Field label="Kod weryfikacyjny z aplikacji">
                <input
                  className="input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={mfaEnableCode}
                  onChange={(event) => setMfaEnableCode(event.target.value)}
                  data-testid="settings-mfa-enable-code"
                />
              </Field>
              <div className="settings-form__actions">
                <button type="button" className="btn btn--ghost" onClick={closeMfaPanel}>
                  Anuluj
                </button>
                <button className="btn" type="submit" disabled={busy} data-testid="settings-mfa-enable-save">
                  Włącz MFA
                </button>
              </div>
            </form>
          ) : (
            <div id={mfaFormId} className="settings-form" data-testid="settings-mfa-start">
              <p className="muted">
                Wygenerujemy kod QR i kody zapasowe. Po włączeniu MFA logowanie hasłem będzie
                wymagało dodatkowego kodu.
              </p>
              <button
                type="button"
                className="btn"
                disabled={busy}
                data-testid="settings-mfa-setup"
                onClick={() => void onMfaSetup()}
              >
                Rozpocznij konfigurację
              </button>
            </div>
          )
        ) : null}
      </section>

      <section className="panel settings-panel">
        <div className="panel__header">
          <div>
            <h2 className="panel__title">Hasło administratora</h2>
            <p className="muted">
              {status?.has_admin_password
                ? "Hasło administratora jest ustawione."
                : "Hasło administratora nie jest jeszcze ustawione."}
            </p>
          </div>
          <button
            type="button"
            className="btn btn--ghost"
            aria-expanded={openPanel === "admin"}
            aria-controls={adminFormId}
            data-testid="settings-admin-toggle"
            onClick={() => togglePanel("admin")}
          >
            {openPanel === "admin"
              ? "Anuluj"
              : status?.has_admin_password
                ? "Zmień hasło"
                : "Ustaw hasło"}
          </button>
        </div>

        {openPanel === "admin" ? (
          <form
            id={adminFormId}
            className="settings-form"
            onSubmit={onAdminPassword}
            autoComplete="off"
            data-testid="settings-admin-form"
          >
            <p className="muted">
              Hasło musi mieć co najmniej 8 znaków i nie może być takie samo jak hasło logowania.
              Potwierdza archiwizację, usuwanie i inne wrażliwe operacje.
            </p>
            {status?.has_admin_password ? (
              <Field label="Aktualne hasło administratora">
                <input
                  className="input"
                  type="password"
                  required
                  value={adminForm.current}
                  onChange={(event) =>
                    setAdminForm({ ...adminForm, current: event.target.value })
                  }
                  {...passwordFieldProps(`${fieldNs}-admin-current`)}
                />
              </Field>
            ) : (
              <Field label="Hasło logowania">
                <input
                  className="input"
                  type="password"
                  required
                  value={adminForm.login}
                  onChange={(event) => setAdminForm({ ...adminForm, login: event.target.value })}
                  {...passwordFieldProps(`${fieldNs}-admin-login`)}
                />
              </Field>
            )}
            <Field label="Nowe hasło administratora">
              <input
                className="input"
                type="password"
                minLength={8}
                required
                value={adminForm.next}
                onChange={(event) => setAdminForm({ ...adminForm, next: event.target.value })}
                {...passwordFieldProps(`${fieldNs}-admin-next`)}
              />
            </Field>
            <Field label="Powtórz nowe hasło administratora">
              <input
                className="input"
                type="password"
                minLength={8}
                required
                value={adminForm.confirm}
                onChange={(event) =>
                  setAdminForm({ ...adminForm, confirm: event.target.value })
                }
                {...passwordFieldProps(`${fieldNs}-admin-confirm`)}
              />
            </Field>
            <div className="settings-form__actions">
              <button type="button" className="btn btn--ghost" onClick={closeAdminPanel}>
                Anuluj
              </button>
              <button className="btn" type="submit" disabled={busy}>
                Zapisz hasło administratora
              </button>
            </div>
          </form>
        ) : null}
      </section>

      <section className="panel settings-panel">
        <div className="panel__header">
          <div>
            <h2 className="panel__title">Potwierdzanie operacji hasłem</h2>
            <p className="muted">
              Przy zwykłej edycji wykotów i stanówki hasło administratora nie jest wymagane.
              Trwałe usunięcie grupy z archiwum zawsze wymaga hasła. Możesz włączyć dodatkowe
              potwierdzenie hasłem przy edycji poniżej.
            </p>
          </div>
          <button
            type="button"
            className="btn btn--ghost"
            aria-expanded={openPanel === "ops"}
            aria-controls={opsFormId}
            data-testid="settings-ops-toggle"
            onClick={() => togglePanel("ops")}
          >
            {openPanel === "ops" ? "Anuluj" : "Zmień"}
          </button>
        </div>

        {openPanel === "ops" ? (
          <form
            id={opsFormId}
            className="settings-form"
            onSubmit={onOpsPreferences}
            autoComplete="off"
            data-testid="settings-ops-form"
          >
            <p className="muted">
              Domyślnie edycja wykotów i stanówki nie wymaga hasła administratora. Zaznacz opcję
              poniżej, jeśli chcesz wymagać hasła przy każdej takiej zmianie. Nie obejmuje to
              trwałego usunięcia całej grupy z archiwum — tam hasło jest zawsze wymagane. Zmianę
              preferencji trzeba potwierdzić hasłem logowania.
            </p>
            <label className="list__check">
              <input
                type="checkbox"
                checked={!opsForm.skip_birth_edit_password}
                data-testid="settings-skip-password"
                onChange={(event) =>
                  setOpsForm({
                    ...opsForm,
                    skip_birth_edit_password: !event.target.checked,
                  })
                }
              />
              <span>Wymagaj hasła administratora przy edycji wykotów i stanówki</span>
            </label>
            <Field label="Hasło logowania (potwierdzenie)">
              <input
                className="input"
                type="password"
                required
                value={opsForm.login_password}
                onChange={(event) =>
                  setOpsForm({ ...opsForm, login_password: event.target.value })
                }
                {...passwordFieldProps(`${fieldNs}-ops-login`)}
              />
            </Field>
            <div className="settings-form__actions">
              <button type="button" className="btn btn--ghost" onClick={closeOpsPanel}>
                Anuluj
              </button>
              <button
                className="btn"
                type="submit"
                disabled={busy}
                data-testid="settings-ops-save"
              >
                Zapisz
              </button>
            </div>
          </form>
        ) : null}
      </section>

      <section className="panel settings-panel settings-panel--danger">
        <div className="panel__header">
          <div>
            <h2 className="panel__title">Strefa niebezpieczna</h2>
            <p className="muted">
              Operacje trwałe lub z okresem karencji 14 dni. Wymagają dodatkowego potwierdzenia.
            </p>
          </div>
        </div>

        {appContentStatus?.active && appContentStatus.can_restore ? (
          <div className="banner banner--warn" data-testid="app-content-restore-banner">
            <p>
              Dane aplikacji zostały wyczyszczone. Możesz je przywrócić do{" "}
              {appContentStatus.purge_at
                ? new Date(appContentStatus.purge_at).toLocaleString("pl-PL")
                : "—"}
              .
            </p>
            <button
              type="button"
              className="btn btn--ghost"
              data-testid="settings-restore-toggle"
              onClick={() => togglePanel("restore")}
            >
              {openPanel === "restore" ? "Anuluj" : "Przywróć dane"}
            </button>
          </div>
        ) : null}

        {openPanel === "restore" ? (
          <form className="settings-form" onSubmit={onRestoreAppContent} autoComplete="off">
            <Field label="Hasło administratora">
              <input
                className="input"
                type="password"
                required
                value={restoreForm.password}
                onChange={(event) => setRestoreForm({ password: event.target.value })}
                {...passwordFieldProps(`${fieldNs}-restore-admin`)}
              />
            </Field>
            <div className="settings-form__actions">
              <button type="submit" className="btn" disabled={busy}>
                Przywróć zawartość aplikacji
              </button>
            </div>
          </form>
        ) : null}

        <div className="settings-danger__item">
          <div>
            <h3 className="settings-danger__title">Wyczyść zawartość aplikacji</h3>
            <p className="muted">
              Usuwa stado, wykoty, stanówki, finanse i pozostałe dane stada. Konto pozostaje.
              Przez {ACCOUNT_DELETION_DAYS} dni możesz przywrócić dane z kopii zapasowej.
            </p>
          </div>
          <button
            type="button"
            className="btn btn--danger"
            data-testid="settings-clear-toggle"
            disabled={Boolean(appContentStatus?.active)}
            onClick={() => togglePanel("clear")}
          >
            {openPanel === "clear" ? "Anuluj" : "Wyczyść dane"}
          </button>
        </div>

        {openPanel === "clear" ? (
          <form className="settings-form" onSubmit={onClearAppContent} autoComplete="off">
            <p className="muted">
              Wpisz frazę <strong>{APP_CONTENT_CLEAR_PHRASE}</strong> oraz hasło administratora.
            </p>
            <Field label="Fraza potwierdzająca">
              <input
                className="input"
                required
                value={clearForm.confirm_phrase}
                onChange={(event) =>
                  setClearForm({ ...clearForm, confirm_phrase: event.target.value })
                }
              />
            </Field>
            <Field label="Hasło administratora">
              <input
                className="input"
                type="password"
                required
                value={clearForm.password}
                onChange={(event) => setClearForm({ ...clearForm, password: event.target.value })}
                {...passwordFieldProps(`${fieldNs}-clear-admin`)}
              />
            </Field>
            <label className="list__check">
              <input
                type="checkbox"
                checked={clearForm.understood}
                onChange={(event) =>
                  setClearForm({ ...clearForm, understood: event.target.checked })
                }
              />
              <span>Rozumiem, że dane zostaną usunięte (z możliwością cofnięcia w 14 dni)</span>
            </label>
            <div className="settings-form__actions">
              <button type="submit" className="btn btn--danger" disabled={busy}>
                Wyczyść zawartość aplikacji
              </button>
            </div>
          </form>
        ) : null}

        <div className="settings-danger__item">
          <div>
            <h3 className="settings-danger__title">Usunięcie konta</h3>
            <p className="muted">
              Konto zostanie dezaktywowane, a po {ACCOUNT_DELETION_DAYS} dniach trwale usunięte.
              Link anulowania wyślemy na {userEmail || "Twój adres e-mail"}.
            </p>
          </div>
          <button
            type="button"
            className="btn btn--danger"
            data-testid="settings-delete-account-toggle"
            onClick={() => togglePanel("delete-account")}
          >
            {openPanel === "delete-account" ? "Anuluj" : "Usuń konto"}
          </button>
        </div>

        {openPanel === "delete-account" ? (
          <form className="settings-form" onSubmit={onDeleteAccount} autoComplete="off">
            <Field label="Adres e-mail (potwierdzenie)">
              <input
                className="input"
                type="email"
                required
                value={deleteAccountForm.email}
                onChange={(event) =>
                  setDeleteAccountForm({ ...deleteAccountForm, email: event.target.value })
                }
              />
            </Field>
            {status?.has_login_password ? (
              <Field label="Hasło logowania">
                <input
                  className="input"
                  type="password"
                  required
                  value={deleteAccountForm.login_password}
                  onChange={(event) =>
                    setDeleteAccountForm({
                      ...deleteAccountForm,
                      login_password: event.target.value,
                    })
                  }
                  {...passwordFieldProps(`${fieldNs}-delete-login`)}
                />
              </Field>
            ) : (
              <p className="muted">Konto Google — wystarczy potwierdzenie adresem e-mail.</p>
            )}
            <label className="list__check">
              <input
                type="checkbox"
                checked={deleteAccountForm.understood}
                onChange={(event) =>
                  setDeleteAccountForm({
                    ...deleteAccountForm,
                    understood: event.target.checked,
                  })
                }
              />
              <span>Rozumiem, że konto zostanie usunięte po {ACCOUNT_DELETION_DAYS} dniach</span>
            </label>
            <div className="settings-form__actions">
              <button type="submit" className="btn btn--danger" disabled={busy}>
                Złóż wniosek o usunięcie konta
              </button>
            </div>
          </form>
        ) : null}
      </section>
    </main>
  );
}
